import { useState, useEffect, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";

// Pages
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import ResendVerificationPage from "./pages/ResendVerificationPage";
import DashboardPage from "./pages/DashboardPage";
import ProjectsPage from "./pages/ProjectsPage";
import ProjectDetailPage from "./pages/ProjectDetailPage";
import SchedulePage from "./pages/SchedulePage";
import ControlTowerPage from "./pages/ControlTowerPage";
import ProcurementPage from "./pages/ProcurementPage";
import WorkforcePage from "./pages/WorkforcePage";
import SettingsPage from "./pages/SettingsPage";
import GanttPage from "./pages/GanttPage";
import DocumentsPage from "./pages/DocumentsPage";
import ApprovalsPage from "./pages/ApprovalsPage";
import CalendarsPage from "./pages/CalendarsPage";
import ContactsPage from "./pages/ContactsPage";
import CompaniesPage from "./pages/CompaniesPage";
import OpportunitiesPage from "./pages/OpportunitiesPage";
import DiscoverPage from "./pages/DiscoverPage";
import MarketplacePage from "./pages/MarketplacePage";
import EmailMarketingPage from "./pages/EmailMarketingPage";
import MessagesPage from "./pages/MessagesPage";
import OperatorPage from "./pages/OperatorPage";
import BidPackageWorkspacePage from "./pages/BidPackageWorkspacePage";
import BuildMyProjectPage from "./pages/BuildMyProjectPage";
import ExchangeProvidersPage from "./pages/ExchangeProvidersPage";
import ProviderPassportPage from "./pages/ProviderPassportPage";
import NotFoundPage from "./pages/NotFoundPage";
import InternalOnly from "./components/workflow/InternalOnly";
import AcceptInvitationPage from "./pages/AcceptInvitationPage";
import PortalLoginPage from "./pages/PortalLoginPage";
import PortalRegisterPage from "./pages/PortalRegisterPage";
import PortalClaimPage from "./pages/PortalClaimPage";
import PortalHomePage from "./pages/PortalHomePage";
import MarketplaceInvitePage from "./pages/MarketplaceInvitePage";
import Layout from "./components/Layout";
import { clearBetaSignals } from "./lib/betaTelemetry";

import { API_BASE_URL } from "./lib/backendUrl";
export const API = API_BASE_URL;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// Store interceptor IDs for cleanup
let requestInterceptorId = null;
let responseInterceptorId = null;

// Axios interceptor setup
const setupAxiosInterceptors = (getToken, logout) => {
  if (requestInterceptorId !== null) {
    axios.interceptors.request.eject(requestInterceptorId);
  }
  if (responseInterceptorId !== null) {
    axios.interceptors.response.eject(responseInterceptorId);
  }

  requestInterceptorId = axios.interceptors.request.use(
    (config) => {
      const token = getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  responseInterceptorId = axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        logout();
      }
      return Promise.reject(error);
    }
  );
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-primary text-xl">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  // null means the authenticated session has not answered yet; [] is the
  // authoritative answer "none". Both offer no controls, but only the latter
  // may be presented as a denial.
  const [capabilities, setCapabilities] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { token: newToken, user: userData, tenant: tenantData } = response.data;

      localStorage.setItem("token", newToken);
      setToken(newToken);
      setUser(userData);
      setTenant(tenantData);
      setCapabilities(null);

      toast.success("Welcome back!");
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || "Login failed";
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const register = async (email, password, fullName, companyName) => {
    try {
      const response = await axios.post(`${API}/auth/register`, {
        email,
        password,
        full_name: fullName,
        company_name: companyName,
      });
      const { token: newToken, user: userData, tenant: tenantData } = response.data;

      localStorage.setItem("token", newToken);
      setToken(newToken);
      setUser(userData);
      setTenant(tenantData);
      setCapabilities(null);

      toast.success("Account created successfully!");
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || "Registration failed";
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
    setTenant(null);
    /**
     * The friction buffer holds project ids and route paths from the session
     * that is ending. It is module-level and would otherwise survive into the
     * next sign-in on this browser, letting one tenant's ids ride along on
     * another tenant's feedback report.
     */
    clearBetaSignals();
    toast.info("Logged out successfully");
  };

  useEffect(() => {
    const fetchUser = async () => {
      if (token) {
        setCapabilities(null);
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setUser(response.data.user);
          setTenant(response.data.tenant);
          // WHAT THIS USER MAY DO, as computed by the backend resolver that
          // also enforces it. Not derived from the role here: a role string
          // is not an authority answer, and deriving one was the duplication
          // issue #85 asked us to remove.
          //
          // Anything that is not an array becomes an empty list, so a server
          // that omits the field offers no controls rather than falling back
          // to a guess.
          setCapabilities(
            Array.isArray(response.data.capabilities)
              ? response.data.capabilities
              : []
          );
        } catch (error) {
          localStorage.removeItem("token");
          setToken(null);
          setCapabilities([]);
        }
      } else {
        // No token: no session, and therefore no capabilities. Cleared
        // explicitly so a logout cannot leave the previous user's answer
        // behind for the next render.
        setCapabilities([]);
      }
      setLoading(false);
    };

    const getToken = () => localStorage.getItem("token");
    setupAxiosInterceptors(getToken, logout);
    fetchUser();
  }, [token]);

  return (
    <AuthContext.Provider
      value={{
        user, tenant, token, login, register, logout, loading, capabilities,
        capabilitiesEstablished: Array.isArray(capabilities),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/resend-verification" element={<ResendVerificationPage />} />
          <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
          <Route path="/portal/login" element={<PortalLoginPage />} />
          <Route path="/portal/register" element={<PortalRegisterPage />} />
          <Route path="/portal/claim" element={<PortalClaimPage />} />
          <Route path="/portal" element={<PortalHomePage />} />
          <Route path="/marketplace/invite/:token" element={<MarketplaceInvitePage />} />
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <Layout>
                  <Routes>
                    <Route path="/" element={<DashboardPage />} />
                    <Route path="/dashboard" element={<DashboardPage />} />
                    <Route path="/projects" element={<ProjectsPage />} />
                    <Route path="/projects/:projectId" element={<ProjectDetailPage />} />
                    <Route path="/projects/:projectId/gantt" element={<GanttPage />} />
                    <Route path="/projects/:projectId/documents" element={<DocumentsPage />} />
                    <Route path="/schedule" element={<SchedulePage />} />
                    <Route path="/control-tower" element={<InternalOnly><ControlTowerPage /></InternalOnly>} />
                    <Route path="/exchange" element={<InternalOnly><BuildMyProjectPage /></InternalOnly>} />
                    <Route path="/exchange/build" element={<InternalOnly><BuildMyProjectPage /></InternalOnly>} />
                    <Route path="/exchange/providers" element={<InternalOnly><ExchangeProvidersPage /></InternalOnly>} />
                    <Route path="/exchange/providers/:organizationId" element={<InternalOnly><ProviderPassportPage /></InternalOnly>} />
                    <Route path="/procurement" element={<InternalOnly><ProcurementPage /></InternalOnly>} />
                    <Route path="/procurement/packages/:packageId" element={<InternalOnly><BidPackageWorkspacePage /></InternalOnly>} />
                    <Route path="/workforce" element={<WorkforcePage />} />
                    <Route path="/approvals" element={<InternalOnly><ApprovalsPage /></InternalOnly>} />
                    <Route path="/calendars" element={<CalendarsPage />} />
                    {/* These five are flagged `internal: true` in the shell's
                        nav, which only HIDES them. Hiding is not a boundary —
                        the route has to be closed too, or an external
                        subcontractor account reaches the tenant's contacts,
                        companies, pipeline, discovery and marketing surfaces
                        by typing the URL. */}
                    <Route path="/contacts" element={<InternalOnly><ContactsPage /></InternalOnly>} />
                    <Route path="/companies" element={<InternalOnly><CompaniesPage /></InternalOnly>} />
                    <Route path="/opportunities" element={<InternalOnly><OpportunitiesPage /></InternalOnly>} />
                    <Route path="/discover" element={<InternalOnly><DiscoverPage /></InternalOnly>} />
                    <Route path="/marketplace" element={<MarketplacePage />} />
                    <Route path="/email" element={<InternalOnly><EmailMarketingPage /></InternalOnly>} />
                    <Route path="/messages" element={<MessagesPage />} />
                    <Route path="/operator" element={<InternalOnly><OperatorPage /></InternalOnly>} />
                    <Route path="/settings" element={<SettingsPage />} />
                    <Route path="*" element={<NotFoundPage />} />
                  </Routes>
                </Layout>
              </ProtectedRoute>
            }
          />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
